import UploadFile from "@mui/icons-material/UploadFile";
import { Button, ButtonProps } from "@mui/material";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useState } from "react";

import { ManifestReviewDialog } from "./ManifestReviewDialog";
import { ManifestUploadDialog } from "./ManifestUploadDialog";

type AddManifestButtonProps = {
  tenantId: string;
  facilityId?: string;
  manifestTrackingNumber?: string;
  generatorId?: string;
  /** MUI Button size — defaults to "medium" */
  size?: ButtonProps["size"];
  /**
   * Called once the review is applied — the review dialog's own completion
   * signal, which fires after the last document's save succeeds, not when the
   * dialog is merely closed. A list host uses it to refetch. Mirrors
   * `AddBillOfLadingButton`'s `onSaved`, minus its saved id: the manifest
   * review dialog reports completion without one.
   */
  onSaved?: () => void;
  /**
   * Opens the upload dialog from outside. Omit this and the component owns
   * its dialog state and renders its own "Add Manifest" button, as it always
   * has. Pass it and the component renders the dialogs alone — the caller
   * supplies the trigger and holds the open state, which lets a host with its
   * own control (the merged list's split button) drive this flow without
   * reimplementing it. The review step that follows an upload stays internal
   * either way.
   */
  open?: boolean;
  /** Called with the upload dialog's new open state. */
  onOpenChange?: (open: boolean) => void;
};
export function AddManifestButton({
  tenantId,
  facilityId,
  manifestTrackingNumber,
  generatorId,
  size,
  onSaved,
  open: controlledOpen,
  onOpenChange,
}: AddManifestButtonProps) {
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  const [ownOpen, setOwnOpen] = useState(false);
  const [reviewDocumentIds, setReviewDocumentIds] = useState<string[] | null>(null);

  if (!canQueueManifestExtraction) return null;

  const controlled = controlledOpen !== undefined;
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };

  return (
    <>
      {!controlled && (
        <Button
          variant="contained"
          size={size}
          startIcon={<UploadFile />}
          onClick={() => setOpen(true)}
        >
          Add Manifest
        </Button>
      )}

      {open && (
        <ManifestUploadDialog
          open
          onClose={() => setOpen(false)}
          tenantId={tenantId}
          facilityId={facilityId}
          manifestTrackingNumber={manifestTrackingNumber}
          generatorId={generatorId}
          onExtractionReady={setReviewDocumentIds}
        />
      )}

      {reviewDocumentIds && (
        <ManifestReviewDialog
          onClose={() => setReviewDocumentIds(null)}
          onComplete={onSaved}
          documentIds={reviewDocumentIds}
        />
      )}
    </>
  );
}
